// Centralized edge creation utilities
import { Node, Position } from '@xyflow/react';
import { createContextLogger } from './logger';

const logger = createContextLogger('edgeUtils');

// Edge condition types
export type EdgeCondition = 'unconditional' | 'true' | 'false' | 'info' | 'error_handling';

// Edge data interface
export interface EdgeData {
  condition: EdgeCondition;
  enabled: boolean;
  fields: any[]; // Will be populated by store's addEdge function
}

// Edge object interface
export interface EdgeObject {
  id: string;
  source: string;
  target: string;
  type: string;
  data: EdgeData;
}

// Parameters for creating edge objects
export interface CreateEdgeObjectParams {
  source: string;
  target: string;
  type?: string;
  enabled?: boolean;
  condition?: EdgeCondition;
  id?: string | null;
}

// Parameters for createAndAddEdge function
interface CreateAndAddEdgeParams {
  source: string;
  target: string;
  addEdgeToStore: (edge: EdgeObject, skipHistory?: boolean) => void;
  type?: string;
  enabled?: boolean;
  condition?: EdgeCondition;
  skipHistory?: boolean;
}

// ReactFlow connection parameters
export interface ConnectionParams {
  source: string;
  target: string;
  sourceHandle?: string | null;
  targetHandle?: string | null;
}

// Generate a unique edge ID
export const generateEdgeId = (source: string, target: string): string => {
  return `edge_${source}_${target}_${Date.now()}`;
};

// Create a standardized edge object
export const createEdgeObject = ({
  source,
  target,
  type = 'custom',
  enabled = true,
  condition = 'unconditional',
  id = null,
}: CreateEdgeObjectParams): EdgeObject => {
  const edgeId = id || generateEdgeId(source, target);

  return {
    id: edgeId,
    source,
    target,
    type,
    data: {
      condition,
      enabled,
      fields: [], // Will be populated by store's addEdge function
    },
  };
};

// Centralized edge creation function that uses the store
export const createAndAddEdge = ({
  source,
  target,
  addEdgeToStore,
  type = 'custom',
  enabled = true,
  condition = 'unconditional',
  skipHistory = false,
}: CreateAndAddEdgeParams): EdgeObject | null => {
  if (!source || !target) {
    logger.error('createAndAddEdge: source and target are required', { source, target });
    return null;
  }

  if (!addEdgeToStore) {
    logger.error('createAndAddEdge: addEdgeToStore function is required');
    return null;
  }

  const newEdge = createEdgeObject({
    source,
    target,
    type,
    enabled,
    condition,
  });

  logger.debug('Creating edge:', {
    edgeId: newEdge.id,
    source,
    target,
    enabled,
    condition,
  });

  addEdgeToStore(newEdge, skipHistory);
  return newEdge;
};

// Helper for ReactFlow onConnect callback
export const createOnConnectHandler = (addEdgeToStore: (edge: EdgeObject, skipHistory?: boolean) => void) => {
  return (params: ConnectionParams): void => {
    createAndAddEdge({
      source: params.source,
      target: params.target,
      addEdgeToStore,
      enabled: true, // Drag-and-drop connections should be enabled by default
      skipHistory: false, // Don't skip history for manual drag-and-drop connections
    });
  };
};

// Helper function to calculate edge parameters for floating edges
// Based on: https://reactflow.dev/examples/edges/floating-edges
interface EdgeParams {
  sx: number;
  sy: number;
  tx: number;
  ty: number;
  sourcePos: Position;
  targetPos: Position;
}

export function getEdgeParams(source: Node, target: Node): EdgeParams {
  const sourceIntersectionPoint = getNodeIntersection(source, target);
  const targetIntersectionPoint = getNodeIntersection(target, source);

  const sourcePos = getEdgePosition(source, sourceIntersectionPoint);
  const targetPos = getEdgePosition(target, targetIntersectionPoint);

  return {
    sx: sourceIntersectionPoint.x,
    sy: sourceIntersectionPoint.y,
    tx: targetIntersectionPoint.x,
    ty: targetIntersectionPoint.y,
    sourcePos,
    targetPos,
  };
}

function getNodeIntersection(intersectionNode: Node, targetNode: Node) {
  // https://math.stackexchange.com/questions/1724792/an-algorithm-for-finding-the-intersection-point-between-a-center-of-vision-and-a
  const { width: intersectionNodeWidth, height: intersectionNodeHeight } =
    intersectionNode.measured || { width: 48, height: 48 };
  const intersectionNodePosition = intersectionNode.internals?.positionAbsolute || intersectionNode.positionAbsolute || { x: 0, y: 0 };
  const targetPosition = targetNode.internals?.positionAbsolute || targetNode.positionAbsolute || { x: 0, y: 0 };

  const w = intersectionNodeWidth / 2;
  const h = intersectionNodeHeight / 2;

  const x2 = intersectionNodePosition.x + w;
  const y2 = intersectionNodePosition.y + h;
  const x1 = targetPosition.x + (targetNode.measured?.width || 48) / 2;
  const y1 = targetPosition.y + (targetNode.measured?.height || 48) / 2;

  const xx1 = (x1 - x2) / (2 * w) - (y1 - y2) / (2 * h);
  const yy1 = (x1 - x2) / (2 * w) + (y1 - y2) / (2 * h);
  const a = 1 / (Math.abs(xx1) + Math.abs(yy1) || 1);
  const xx3 = a * xx1;
  const yy3 = a * yy1;
  const x = w * (xx3 + yy3) + x2;
  const y = h * (-xx3 + yy3) + y2;

  return { x, y };
}

function getEdgePosition(node: Node, intersectionPoint: { x: number; y: number }) {
  const n = { ...node.internals.positionAbsolute, ...node };
  const nx = Math.round(n.x);
  const ny = Math.round(n.y);
  const px = Math.round(intersectionPoint.x);
  const py = Math.round(intersectionPoint.y);

  if (px <= nx + 1) {
    return Position.Left;
  }
  if (px >= nx + (node.measured?.width || 48) - 1) {
    return Position.Right;
  }
  if (py <= ny + 1) {
    return Position.Top;
  }
  if (py >= ny + (node.measured?.height || 48) - 1) {
    return Position.Bottom;
  }

  return Position.Top;
}
